import { parseJwt } from "@utils/parseJwt";
import { format } from "date-fns";
import { useRouter } from "next/router";
import { useEffect, useReducer, useState } from "react";
import { apiAcceptTransfer, apiCompleteTransfer } from "../transferApi";

export enum TransferState {
  PENDING,
  ACCEPTED,
  COMPLETED,
}

export enum TransferActions {
  ACCEPT_TRANSFER,
  COMPLETE_TRANSFER,
}

interface ReducerState {
  status: TransferActions;
  intentToken: string;
}
interface Action {
  type: TransferActions;
  intentToken?: string;
}

const transferReducer = (state: ReducerState, action: Action) => {
  switch (action.type) {
    case TransferActions.ACCEPT_TRANSFER: {
      return {
        ...state,
        status: TransferState.ACCEPTED,
        intentToken: action.intentToken,
      };
    }
    case TransferActions.COMPLETE_TRANSFER: {
      return {
        ...state,
        status: TransferState.COMPLETED,
      };
    }
    default: {
      return state;
    }
  }
};
export interface TransferJWT {
  transferee: string;
  transferer: string;
  transferer_email: string;
  iat: number;
  exp: number;
}

export const useCompleteTransfer = () => {
  const [{ status, intentToken }, dispatch] = useReducer(transferReducer, {
    status: TransferState.PENDING,
    intentToken: null,
  });

  const router = useRouter();
  const [token, setToken] = useState<string>();
  const [transfererEmail, setTransferEmail] = useState<string>();
  const [created, setCreated] = useState<string>();
  const [apiError, setApiError] = useState<string | null>();

  useEffect(() => {
    if (Object.keys(router.query).length === 0) {
      return;
    }

    const result = parseJwt<TransferJWT>(router.query);
    const {
      token,
      data: { transferer_email, iat },
    } = result;

    setToken(token);
    setTransferEmail(transferer_email);
    setCreated(format(new Date(iat * 1000), "MM/dd/yyyy"));
  }, [router.query]);

  useEffect(() => {
    if (status === TransferState.COMPLETED) {
      router.push("/account");
    }
  }, [status, router]);

  const acceptTransfer = async () => {
    await apiAcceptTransfer(token)
      .then((res) => {
        const { client_secret } = res;
        if (!client_secret) {
          setApiError("Unable to accept transfer. Please contact Member Experience.");
        } else {
          dispatch({ type: TransferActions.ACCEPT_TRANSFER, intentToken: client_secret });
          if (apiError) {
            setApiError(null);
          }
        }
      })
      .catch(() => {
        setApiError("Unable to accept transfer. Please contact Member Experience.");
      });
  };

  const completeTransfer = async ({
    paymentId,
    postalCode,
  }: {
    paymentId: string;
    postalCode: string;
  }) => {
    await apiCompleteTransfer(token, paymentId, postalCode)
      .then(() => {
        dispatch({ type: TransferActions.COMPLETE_TRANSFER });
        if (apiError) {
          setApiError(null);
        }
      })
      .catch(() => {
        setApiError("Unable to Complete Transfer. Please contact Member Experience.");
      });
  };
  return {
    status,
    acceptTransfer,
    completeTransfer,
    created,
    transfererEmail,
    intentToken,
    apiError,
    setApiError,
  };
};
